/**
 * SuperadminPage - Container Component
 * Håndterer organisasjonsadministrasjon for superadmin-brukere
 * Følger REACT_PATTERNS.md: Container/Presentational Split
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, CheckCircle2, X } from 'lucide-react';
import { superadminAPI, type Organisasjon, type CreateOrganisasjonData, type UpdateOrganisasjonData } from '../services/api';
import OrganisasjonList from '../components/superadmin/OrganisasjonList';
import OrganisasjonDialog from '../components/superadmin/OrganisasjonDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AppHeader from '../components/layout/AppHeader';
import AdminAuditLogg from '../components/settings/AdminAuditLogg';
import { Card } from '@/components/ui/card';
import { useAuth } from '../contexts/AuthContext';

const SuperadminPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { velgOrgKontekst, orgKontekst } = useAuth();
  
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingOrg, setEditingOrg] = React.useState<Organisasjon | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [pendingUpdate, setPendingUpdate] = React.useState<{ id: number; data: UpdateOrganisasjonData } | null>(null);
  const [oauthLinkCounts, setOauthLinkCounts] = React.useState<{ googleCount: number; facebookCount: number } | null>(null);

  // Hent alle organisasjoner
  const { data, isLoading } = useQuery({
    queryKey: ['superadmin', 'organisasjoner'],
    queryFn: () => superadminAPI.getOrganisasjoner(),
  });

  // Opprett organisasjon
  const createMutation = useMutation({
    mutationFn: (data: CreateOrganisasjonData) => superadminAPI.createOrganisasjon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'organisasjoner'] });
      setDialogOpen(false);
      setEditingOrg(null);
      setError(null);
      setSuccessMessage('Organisasjonen og første admin-bruker er opprettet.');
      setTimeout(() => setSuccessMessage(null), 5000);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Kunne ikke opprette organisasjon');
      setSuccessMessage(null);
    },
  });

  // Oppdater organisasjon
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateOrganisasjonData }) =>
      superadminAPI.updateOrganisasjon(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'organisasjoner'] });
      queryClient.invalidateQueries({ queryKey: ['oauthSettings'] }); // Invalider OAuth settings cache
      setDialogOpen(false);
      setEditingOrg(null);
      setError(null);
      setConfirmDialogOpen(false);
      setPendingUpdate(null);
      setOauthLinkCounts(null);
      
      // Vis melding om slettede koblinger
      const { removedLinks } = response;
      const totalRemoved = removedLinks.google + removedLinks.facebook;
      if (totalRemoved > 0) {
        const parts = [];
        if (removedLinks.google > 0) {
          parts.push(`${removedLinks.google} Google-kobling${removedLinks.google > 1 ? 'er' : ''}`);
        }
        if (removedLinks.facebook > 0) {
          parts.push(`${removedLinks.facebook} Facebook-kobling${removedLinks.facebook > 1 ? 'er' : ''}`);
        }
        setSuccessMessage(`Endringene er lagret. ${parts.join(' og ')} ble slettet.`);
      } else {
        setSuccessMessage('Endringene er lagret.');
      }
      setTimeout(() => setSuccessMessage(null), 5000);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Kunne ikke oppdatere organisasjon');
      setSuccessMessage(null);
      setConfirmDialogOpen(false);
      setPendingUpdate(null);
      setOauthLinkCounts(null);
    },
  });

  const handleCreate = () => {
    setEditingOrg(null);
    setError(null);
    setDialogOpen(true);
  };

  const handleEdit = (org: Organisasjon) => {
    setEditingOrg(org);
    setError(null);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingOrg(null);
    setError(null);
  };

  const handleSubmit = async (data: CreateOrganisasjonData | UpdateOrganisasjonData) => {
    if (editingOrg) {
      const updateData = data as UpdateOrganisasjonData;
      const currentOrg = editingOrg;
      
      // Sjekk om OAuth skrus av
      const googleTurningOff = updateData.allowGoogleOAuth === false && currentOrg.allowGoogleOAuth === true;
      const facebookTurningOff = updateData.allowFacebookOAuth === false && currentOrg.allowFacebookOAuth === true;
      
      if (googleTurningOff || facebookTurningOff) {
        // Hent antall OAuth-koblinger
        try {
          const counts = await superadminAPI.getOAuthLinks(editingOrg.id);
          const hasLinks = (googleTurningOff && counts.googleCount > 0) || 
                          (facebookTurningOff && counts.facebookCount > 0);
          
          if (hasLinks) {
            // Vis bekreftelsesdialog
            setOauthLinkCounts(counts);
            setPendingUpdate({ id: editingOrg.id, data: updateData });
            setConfirmDialogOpen(true);
            return;
          }
        } catch (err: any) {
          console.error('Failed to fetch OAuth link counts:', err);
          // Fortsett med oppdatering hvis vi ikke kan hente antall
        }
      }
      
      // Ingen koblinger å slette, oppdater direkte
      updateMutation.mutate({ id: editingOrg.id, data: updateData });
    } else {
      createMutation.mutate(data as CreateOrganisasjonData);
    }
  };

  const handleGaaInn = async (org: Organisasjon) => {
    try {
      setError(null);
      await velgOrgKontekst(org.id);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke gå inn i organisasjonen');
    }
  };

  const handleConfirmOAuthDisable = () => {
    if (pendingUpdate) {
      updateMutation.mutate(pendingUpdate);
    }
  };

  const handleCancelOAuthDisable = () => {
    setConfirmDialogOpen(false);
    setPendingUpdate(null);
    setOauthLinkCounts(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <AppHeader />
      <main className="max-w-6xl mx-auto p-4 md:p-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Superadmin</h1>
              <p className="text-sm text-muted-foreground">
                Administrer organisasjoner og brukere
              </p>
            </div>
          </div>

          {/* Success/Error messages */}
          {successMessage && (
            <Alert className="bg-status-confirmed/10 border-status-confirmed">
              <CheckCircle2 className="h-4 w-4 text-status-confirmed" />
              <AlertDescription className="flex items-center justify-between">
                <span>{successMessage}</span>
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="ml-4 hover:opacity-70"
                >
                  <X className="h-4 w-4" />
                </button>
              </AlertDescription>
            </Alert>
          )}

          {/* Veiledning når ingen organisasjon er valgt */}
          {!orgKontekst && (
            <Alert>
              <AlertDescription>
                Velg en organisasjon for å se og endre data i den. Bruk «Velg organisasjon» øverst, eller «Velg» i tabellen.
              </AlertDescription>
            </Alert>
          )}

          {/* Organisasjoner liste */}
          <OrganisasjonList
            organisasjoner={data?.organisasjoner || []}
            onEdit={handleEdit}
            onCreate={handleCreate}
            onGaaInn={handleGaaInn}
            loading={isLoading}
          />

          {/* Feil fra «Velg» vises utenfor dialogen (dialogen er lukket) */}
          {error && !dialogOpen && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Plattformlogg */}
          <Card className="p-6 shadow-sm">
            <h2 className="mb-4 text-2xl font-semibold">Plattformlogg</h2>
            <AdminAuditLogg kilde="plattform" />
          </Card>

          {/* Dialog */}
          <OrganisasjonDialog
            open={dialogOpen}
            onClose={handleClose}
            onSubmit={handleSubmit}
            organisasjon={editingOrg}
            loading={createMutation.isPending || updateMutation.isPending}
            error={error}
          />

          {/* Bekreftelsesdialog for OAuth-deaktivering */}
          <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Deaktiver OAuth-innlogging?</AlertDialogTitle>
                <AlertDialogDescription>
                  {oauthLinkCounts && (
                    <>
                      {pendingUpdate?.data.allowGoogleOAuth === false && oauthLinkCounts.googleCount > 0 && (
                        <p className="mb-2">
                          <strong>{oauthLinkCounts.googleCount}</strong> bruker{oauthLinkCounts.googleCount > 1 ? 'e' : ''} har koblet Google-kontoen sin.
                        </p>
                      )}
                      {pendingUpdate?.data.allowFacebookOAuth === false && oauthLinkCounts.facebookCount > 0 && (
                        <p className="mb-2">
                          <strong>{oauthLinkCounts.facebookCount}</strong> bruker{oauthLinkCounts.facebookCount > 1 ? 'e' : ''} har koblet Facebook-kontoen sin.
                        </p>
                      )}
                      <p>
                        Alle eksisterende OAuth-koblinger for denne organisasjonen vil bli slettet. 
                        Brukere vil ikke lenger kunne logge inn med {pendingUpdate?.data.allowGoogleOAuth === false && pendingUpdate?.data.allowFacebookOAuth === false 
                          ? 'Google eller Facebook' 
                          : pendingUpdate?.data.allowGoogleOAuth === false 
                            ? 'Google' 
                            : 'Facebook'}.
                      </p>
                    </>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={handleCancelOAuthDisable}>Avbryt</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirmOAuthDisable}>
                  Deaktiver og slett koblinger
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </main>
    </div>
  );
};

export default SuperadminPage;

